/* eslint-disable max-lines */
import {
  NEGATIVE_FILTER_TOKEN,
  SEPARATOR,
  useDownloadIncidentReport,
} from '@hakimo-ui/hakimo/data-access';
import {
  DataVersion,
  IncidentProduct,
  LocationIncident,
  TimePeriod,
  UnifiedAlarmGroup,
} from '@hakimo-ui/hakimo/types';
import { Cell, Column, Row, TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  appendVersionParam,
  getMHFromTimePeriod,
  toast,
} from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { LocationIncidentsFilterValues } from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);

function DownloadReportButton({
  alarmId,
  product,
  dataVersion,
}: {
  alarmId: string | number;
  product: IncidentProduct;
  dataVersion?: DataVersion;
}) {
  const downloadMutation = useDownloadIncidentReport(
    product,
    () => {
      toast('Report downloaded successfully', { type: 'success' });
    },
    (error: Error) => {
      toast(`Failed to download report: ${error.message}`, { type: 'error' });
    },
    // The endpoint resolves the incident by id, and v2/v3 ids live in
    // different tables — without this a v3 row downloads against v2.
    dataVersion
  );

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    downloadMutation.mutate({ alarm_id: alarmId });
  };

  return (
    <Button
      variant="icon"
      onClick={handleDownload}
      disabled={downloadMutation.isLoading}
      loading={downloadMutation.isLoading}
      title="Download Report"
      className="!p-0"
    >
      <ArrowDownTrayIcon className="h-4 w-4" />
    </Button>
  );
}

function getFilterQuery(values: string[], negative: boolean): string {
  const query = values;
  if (negative) {
    query.push(NEGATIVE_FILTER_TOKEN);
  }
  return query.join(SEPARATOR);
}

export function useWithTimeFilterValue<
  T extends { time?: { customRangeStart?: number; customRangeEnd?: number } }
>(currentValue: T, period: TimePeriod) {
  return {
    ...currentValue,
    time: {
      period,
      customRangeStart:
        period === TimePeriod.CUSTOM_RANGE
          ? currentValue.time?.customRangeStart || 0
          : 0,
      customRangeEnd:
        period === TimePeriod.CUSTOM_RANGE
          ? currentValue.time?.customRangeEnd || 0
          : 0,
    },
  } as T & {
    time: {
      period: TimePeriod;
      customRangeStart: number;
      customRangeEnd: number;
    };
  };
}

export function getSearchParams(
  page: number,
  pageSize: number,
  filters: any
): string {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));

  if (filters.status?.values?.length > 0) {
    params.append('statuses', filters.status.values.join(','));
  }

  if (filters.location && filters.location.values.length > 0) {
    const locationIds = filters.location.values.map((item: any) => item.id);
    const query = getFilterQuery(locationIds, filters.location.negative);
    params.append('locationIds', query);
  }

  if (filters.time && filters.time.period !== TimePeriod.ALL_TIME) {
    if (filters.time.period === TimePeriod.CUSTOM_RANGE) {
      const startTime = filters.time.customRangeStart;
      const endTime = filters.time.customRangeEnd;
      if (startTime && startTime > 0) {
        params.append('dateFrom', String(startTime));
      }
      if (endTime && endTime > 0) {
        params.append('dateTo', String(endTime));
      }
    } else {
      const [minutes, hours] = getMHFromTimePeriod(filters.time.period);
      if (hours > 0) {
        params.append('relativeTimeHours', String(hours));
      }
      if (minutes > 0) {
        params.append('relativeTimeMinutes', String(minutes));
      }
    }
  }

  return params.toString();
}

export function isNotDefaultFilter(
  filters: LocationIncidentsFilterValues
): boolean {
  return (
    filters.status.values.length !== 1 ||
    filters.status.values[0] !== ('resolved' as any) ||
    filters.location.values.length > 0 ||
    filters.time.period !== TimePeriod.PAST_24_HOURS
  );
}

// Unified columns shared by RG and Scan. Visibility is controlled by the
// column selector (ColumnsMenu); we don't use showAtBreakpoint anymore.
export const unifiedColumns: Column[] = [
  { id: 'alarm_id', name: 'Alarm ID' },
  { id: 'report', name: 'Report' },
  { id: 'location_name', name: 'Location Name' },
  { id: 'resolved_by', name: 'Incident Resolved By' },
  { id: 'email_sent_at', name: 'Email Sent At' },
  { id: 'email_sent_by', name: 'Email Sent By' },
  { id: 'incident_start_time', name: 'Incident Start Time' },
  { id: 'incident_end_time', name: 'Incident End Time' },
  { id: 'incident_category', name: 'Incident Category' },
  { id: 'resolution_reason', name: 'Resolution Reason' },
  { id: 'tenant_id', name: 'Tenant ID' },
];

// Shown by default; the rest are opt-in via the column selector.
export const defaultVisibleColumns = [
  'alarm_id',
  'report',
  'location_name',
  'resolved_by',
  'email_sent_at',
  'email_sent_by',
  'incident_start_time',
  'incident_category',
];

function getVisibleColumns(shownColumns: string[]): Column[] {
  return unifiedColumns.filter((c) => shownColumns.includes(c.id));
}

// RG times arrive pre-localized from the backend; Scan times are UTC, so we
// localize them with the location timezone to match RG's "... US/Pacific" look.
function formatLocalTime(utcTime?: string | null, tz?: string): string {
  if (!utcTime) {
    return '-';
  }
  const d = dayjs.utc(utcTime);
  return tz
    ? `${d.tz(tz).format('YYYY-MM-DD HH:mm:ss')} ${tz}`
    : `${d.format('YYYY-MM-DD HH:mm:ss')} UTC`;
}

export function getTableData(
  items: LocationIncident[],
  shownColumns: string[],
  onRowClick?: (
    incident: LocationIncident,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void,
  dataVersion?: DataVersion
): TableData {
  const columns = getVisibleColumns(shownColumns);
  const rows: Row[] = items.map<Row>((item) => {
    const cellMap: Record<string, Cell> = {
      alarm_id: {
        value: (
          <div className="truncate font-mono text-sm">
            <button
              className="text-primary-600 hover:text-primary-800 cursor-pointer underline"
              onClick={(e) => {
                e.stopPropagation();
                // Carry the incident's data version into the opened alarm so it
                // reads the same v2/v3 dataset.
                const url = appendVersionParam(
                  `/location-alarms/${item.location_alarm_id}`,
                  dataVersion
                );
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              {item.location_alarm_id}
            </button>
          </div>
        ),
      },
      report: {
        value:
          item.report_path && item.location_alarm_id ? (
            <DownloadReportButton
              alarmId={item.location_alarm_id}
              product="remote-guarding"
              dataVersion={dataVersion}
            />
          ) : null,
      },
      location_name: { value: item.location_name || '-' },
      resolved_by: { value: item.resolved_by || '-' },
      email_sent_at: {
        value: item.email_sent_at
          ? dayjs(item.email_sent_at).format('YYYY-MM-DD HH:mm:ss')
          : '-',
      },
      email_sent_by: { value: item.email_sent_by || '-' },
      incident_start_time: {
        value: item.incident_start_time
          ? String(item.incident_start_time)
          : '-',
      },
      incident_end_time: {
        value: item.incident_end_time ? String(item.incident_end_time) : '-',
      },
      incident_category: { value: item.incident_category || '-' },
      resolution_reason: { value: item.resolution_reason || '-' },
      tenant_id: { value: item.tenant_id || '-' },
    };

    return {
      id: item.id?.toString() || '',
      onClick: onRowClick
        ? (event: React.MouseEvent | React.KeyboardEvent) =>
            onRowClick(item, event)
        : undefined,
      cells: columns.map((col) => cellMap[col.id]),
    };
  });

  return { columns, rows };
}

export function getFormattedExportData(
  allLocationIncidents: LocationIncident[]
) {
  return (
    allLocationIncidents?.map((incident) => ({
      'Alarm ID': incident.location_alarm_id || '-',
      'Location Name': incident.location_name || '-',
      'Incident Resolved By': incident.resolved_by || '-',
      'Email Sent At': incident.email_sent_at
        ? dayjs(incident.email_sent_at).format('YYYY-MM-DD HH:mm:ss')
        : '-',
      'Email Sent By': incident.email_sent_by || '-',
      'Incident Start Time': incident.incident_start_time || '-',
      'Incident End Time': incident.incident_end_time || '-',
      'Incident Category': incident.incident_category || '-',
      'Resolution Reason': incident.resolution_reason || '-',
      'Tenant ID': incident.tenant_id || '-',
    })) || []
  );
}

// Search params for scan data
export function getScanSearchParams(
  page: number,
  pageSize: number,
  period: TimePeriod,
  customRange?: { start: number; end: number },
  filters?: any
): string {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));
  params.append('state', 'Resolved');
  params.append('isEscalated', 'true');

  if (filters?.location && filters.location.values.length > 0) {
    const locationIds = filters.location.values.map((item: any) => item.id);
    const query = getFilterQuery(locationIds, filters.location.negative);
    params.append('locationIds', query);
  }

  if (period !== TimePeriod.ALL_TIME) {
    if (period === TimePeriod.CUSTOM_RANGE && customRange) {
      if (customRange.start) {
        params.append('dateFrom', String(customRange.start));
      }
      if (customRange.end) {
        params.append('dateTo', String(customRange.end));
      }
    } else {
      const [minutes, hours] = getMHFromTimePeriod(period);
      const totalMinutes = minutes + hours * 60;
      if (totalMinutes > 0) {
        params.append('relativeTimeMinutes', String(totalMinutes));
      }
    }
  }

  return params.toString();
}

export function getScanTableData(
  items: UnifiedAlarmGroup[],
  shownColumns: string[],
  onRowClick?: (
    incident: UnifiedAlarmGroup,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void
): TableData {
  const columns = getVisibleColumns(shownColumns);
  const rows: Row[] = items.map<Row>((item) => {
    const tz = item.location?.timezone;
    const cellMap: Record<string, Cell> = {
      alarm_id: {
        value: (
          <div className="truncate font-mono text-sm">
            <button
              className="text-primary-600 hover:text-primary-800 cursor-pointer underline"
              onClick={(e) => {
                e.stopPropagation();
                const url = `/alarm-groups/${item.id}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              {item.id}
            </button>
          </div>
        ),
      },
      report: {
        value: item.report_path ? (
          <DownloadReportButton alarmId={item.id} product="scan" />
        ) : null,
      },
      location_name: { value: item.location?.name || '-' },
      resolved_by: { value: item.resolved_by || '-' },
      email_sent_at: {
        value: item.email_sent_at_utc
          ? dayjs(item.email_sent_at_utc).format('YYYY-MM-DD HH:mm:ss')
          : '-',
      },
      email_sent_by: { value: item.email_sent_by || '-' },
      incident_start_time: {
        value: formatLocalTime(item.incident_start_time_utc, tz),
      },
      incident_end_time: {
        value: formatLocalTime(item.incident_end_time_utc, tz),
      },
      incident_category: { value: item.incident_category || '-' },
      resolution_reason: { value: item.resolution_reason || '-' },
      tenant_id: { value: item.tenantId || '-' },
    };

    return {
      id: item.id.toString(),
      onClick: onRowClick
        ? (event: React.MouseEvent | React.KeyboardEvent) =>
            onRowClick(item, event)
        : undefined,
      cells: columns.map((col) => cellMap[col.id]),
    };
  });

  return { columns, rows };
}

export function getScanFormattedExportData(
  allScanIncidents: UnifiedAlarmGroup[]
) {
  return (
    allScanIncidents?.map((incident) => ({
      'Alarm ID': incident.id || '-',
      'Location Name': incident.location?.name || '-',
      'Incident Resolved By': incident.resolved_by || '-',
      'Email Sent At': incident.email_sent_at_utc
        ? dayjs(incident.email_sent_at_utc).format('YYYY-MM-DD HH:mm:ss')
        : '-',
      'Email Sent By': incident.email_sent_by || '-',
      'Incident Start Time': formatLocalTime(
        incident.incident_start_time_utc,
        incident.location?.timezone
      ),
      'Incident End Time': formatLocalTime(
        incident.incident_end_time_utc,
        incident.location?.timezone
      ),
      'Incident Category': incident.incident_category || '-',
      'Resolution Reason': incident.resolution_reason || '-',
      'Tenant ID': incident.tenantId || '-',
    })) || []
  );
}
